/**
 * ARKO-Kontextfenster — Client-Näherung des nächsten Ollama-Payloads.
 *
 * Bausteine = Body-Builder / Renderer-Builder in
 * `scripts/labor/arko-portal-n8n-code.js` (ARKO_AGENT).
 * Keine Cursor-MCP/Rules/Skills-Kategorien. Kein Tokenizer — Zeichen/4.
 *
 * Limit = Ollama PARAMETER `num_ctx` von `mistral-small3.1:24b` (Labor 22.08.2026:
 * 4096). Die Modellkarte (`context length` 131072) gilt nur, wenn n8n
 * `options.num_ctx` setzt — ARKO tut das nicht.
 */

export const ARKO_CHARS_PER_TOKEN = 4;

/** Effektives Fenster: Ollama Modelfile `num_ctx`, nicht die 128k-Karte. */
export const ARKO_OLLAMA_NUM_CTX = 4096;

/** Nur Dokumentation — nicht als Meter-Limit verwenden. */
export const ARKO_MODEL_CARD_CTX = 131_072;

export const ARKO_HISTORY_CAP_ALLROUNDER = 8;
export const ARKO_HISTORY_CAP_RECHTSFRAGE = 4;
export const ARKO_LEXCORE_SOURCES_CAP = 5;

/** Recherche-Karte ins LLM — hartes Budget (ARKO_WEB_GATE), Zeichen/4. */
export const ARKO_WEB_CARD_MAX_TOKENS = 400;
export const ARKO_WEB_CARD_MAX_CHARS = ARKO_WEB_CARD_MAX_TOKENS * ARKO_CHARS_PER_TOKEN;

/**
 * Allrounder-personaCore (BODY_BUILDER), ohne Kanzlei-Klammer.
 * Grobe Zeichenlänge der Pflicht-Persona — Drift von ein paar 100 Zeichen
 * bleibt unter der Tokenizer-Näherung.
 */
export const ARKO_ALLROUNDER_PERSONA_CHARS = 3600;

/** Rechtsfrage-Persona ohne VERIFIZIERTE-NORMEN-Block (Renderer-Builder). */
export const ARKO_RECHTSFRAGE_PERSONA_CHARS = 2400;

const INTENT_BLOCK_CHARS: Record<string, number> = {
  umformulieren: 72,
  erklaeren: 88,
  document_question: 280,
  zusammenfassen: 390,
  speichern: 92,
  schreiben_erstellen: 1750,
  judicature_recherche: 0,
  web_recherche: 0,
};

const DOC_HEADER = 'Kontext aus Dokumentenansicht:\n';
const MEM_HEADER = 'Kontext aus Anwalts-Gedaechtnis (MEMCORE, on-demand Recall):\n';
const LEX_HEADER = 'VERIFIZIERTE NORMEN (Quelle: LEXCORE):\n';

export type ArkoContextCategoryId =
  | 'system'
  | 'intent'
  | 'handoff'
  | 'extracts'
  | 'history'
  | 'lexcore'
  | 'memcore'
  | 'web'
  | 'draft';

export interface ArkoContextCategory {
  id: ArkoContextCategoryId;
  label: string;
  chars: number;
  tokens: number;
  /** Nur Dateinamen / Kategorienamen — keine Auszugstexte. */
  detail?: string;
}

export interface ArkoContextMessage {
  role?: string;
  text?: string | null;
}

export interface ArkoContextMemItem {
  vermerk_id?: string;
  inhalt?: string | null;
  gueltig_von?: string | null;
}

export interface ArkoContextLexSource {
  norm_id?: string;
  title?: string;
  fragment_text?: string;
}

const HANDOFF_HEADER = 'Arbeitsstand dieser Akte (Handoff, kein Chatverlauf):\n';

export interface ArkoContextUsageInput {
  messages?: ArkoContextMessage[];
  documentContext?: string | null;
  extractTitles?: string[];
  draft?: string | null;
  intent?: string | null;
  memItems?: ArkoContextMemItem[];
  lexSources?: ArkoContextLexSource[];
  /** Gecappter Akten-Handoff — eigene Kategorie, nicht 8 Verlaufsnachrichten. */
  akteHandoff?: string | null;
  /** Recherche-Karte vom ARKORBIT-Gate (nicht Brave-Rohseiten). */
  webCard?: string | null;
}

export interface ArkoContextUsage {
  limitTokens: number;
  usedTokens: number;
  usedChars: number;
  percent: number;
  approximation: true;
  categories: ArkoContextCategory[];
}

export function estimateArkoTokens(chars: number): number {
  if (!Number.isFinite(chars) || chars <= 0) return 0;
  return Math.ceil(chars / ARKO_CHARS_PER_TOKEN);
}

export function formatArkoTokenCount(n: number): string {
  const v = Math.max(0, Math.round(n));
  return v.toLocaleString('de-AT');
}

function historyCapForIntent(intent: string): number {
  return intent === 'rechtsfrage' ? ARKO_HISTORY_CAP_RECHTSFRAGE : ARKO_HISTORY_CAP_ALLROUNDER;
}

function personaCharsForIntent(intent: string): number {
  if (intent === 'judicature_recherche' || intent === 'web_recherche') return 0;
  return intent === 'rechtsfrage' ? ARKO_RECHTSFRAGE_PERSONA_CHARS : ARKO_ALLROUNDER_PERSONA_CHARS;
}

function intentBlockChars(intent: string): number {
  if (!intent) return 0;
  return INTENT_BLOCK_CHARS[intent] ?? 0;
}

function pushCategory(
  out: ArkoContextCategory[],
  id: ArkoContextCategoryId,
  label: string,
  chars: number,
  detail?: string,
): void {
  const n = Math.max(0, Math.round(chars));
  if (n <= 0) return;
  out.push({
    id,
    label,
    chars: n,
    tokens: estimateArkoTokens(n),
    ...(detail ? { detail } : {}),
  });
}

function lexSourcesChars(sources: ArkoContextLexSource[]): number {
  const capped = sources.slice(0, ARKO_LEXCORE_SOURCES_CAP);
  if (capped.length === 0) return 0;
  const body = capped
    .map((s) => {
      const id = String(s.norm_id || '').trim();
      const title = String(s.title || '').trim();
      const frag = String(s.fragment_text || '').trim();
      return `[${id}] ${title}\n${frag}`;
    })
    .join('\n\n---\n\n');
  return LEX_HEADER.length + body.length;
}

function memContextChars(items: ArkoContextMemItem[]): number {
  if (!items.length) return 0;
  const lines = items
    .map((v) => {
      const id = String(v.vermerk_id || '').trim();
      const txt = String(v.inhalt || '').trim();
      const von = String(v.gueltig_von || '').slice(0, 10);
      if (!txt) return '';
      return `- [${id}${von ? ` · ${von}` : ''}] ${txt}`;
    })
    .filter(Boolean);
  if (!lines.length) return 0;
  return MEM_HEADER.length + lines.join('\n').length;
}

/**
 * Schätzt den nächsten Ollama-Prompt (System + Verlauf + User-Teile).
 * LEXCORE-Trail nur wenn `intent=rechtsfrage` und Quellen dieses Turns vorliegen
 * (letzter lexProof als Näherung). JUD geht nicht ins LLM — nur über den Chatverlauf.
 * Web-Recherche: kein Systemprompt, keine Auszüge; nur eine Karte ≤400 Tokens.
 */
export function buildArkoContextUsage(input: ArkoContextUsageInput): ArkoContextUsage {
  const intent = String(input.intent || '').trim();
  const categories: ArkoContextCategory[] = [];
  const skipDocForWeb = intent === 'web_recherche';

  pushCategory(categories, 'system', 'Systemprompt', personaCharsForIntent(intent));

  const intentChars = intentBlockChars(intent);
  pushCategory(
    categories,
    'intent',
    'Auftrag / Chip',
    intentChars,
    intent ? `Intent ${intent}` : undefined,
  );

  const handoff = String(input.akteHandoff || '').trim();
  if (handoff) {
    pushCategory(
      categories,
      'handoff',
      'Akten-Handoff',
      HANDOFF_HEADER.length + handoff.length,
    );
  }

  const doc = skipDocForWeb ? '' : String(input.documentContext || '').trim();
  if (doc) {
    const titles = (input.extractTitles || []).map((t) => t.trim()).filter(Boolean);
    pushCategory(
      categories,
      'extracts',
      'Angehakte Auszüge',
      DOC_HEADER.length + doc.length,
      titles.length ? titles.join(', ') : undefined,
    );
  }

  const historyTexts = (input.messages || [])
    .map((m) => String(m?.text || '').trim())
    .filter((t) => t.length > 0);
  const sliced = historyTexts.slice(-historyCapForIntent(intent));
  const historyChars = sliced.reduce((sum, t) => sum + t.length, 0);
  pushCategory(
    categories,
    'history',
    'Chatverlauf',
    historyChars,
    sliced.length ? `${sliced.length} Nachrichten` : undefined,
  );

  if (intent === 'rechtsfrage') {
    const lexChars = lexSourcesChars(input.lexSources || []);
    pushCategory(
      categories,
      'lexcore',
      'Normen-Belege',
      lexChars,
      lexChars
        ? `bis ${ARKO_LEXCORE_SOURCES_CAP} Normen`
        : undefined,
    );
  }

  pushCategory(categories, 'memcore', 'Anwalts-Gedächtnis', memContextChars(input.memItems || []));

  const webCard = String(input.webCard || '').trim().slice(0, ARKO_WEB_CARD_MAX_CHARS);
  if (webCard) {
    pushCategory(
      categories,
      'web',
      'Recherche-Karte',
      webCard.length,
      `≤${ARKO_WEB_CARD_MAX_TOKENS} Tokens`,
    );
  }

  const draft = String(input.draft || '').trim();
  pushCategory(categories, 'draft', 'Aktueller Auftrag', draft.length);

  const usedChars = categories.reduce((sum, c) => sum + c.chars, 0);
  const usedTokens = estimateArkoTokens(usedChars);
  const percent = ARKO_OLLAMA_NUM_CTX > 0
    ? Math.round((usedTokens / ARKO_OLLAMA_NUM_CTX) * 100)
    : 0;

  return {
    limitTokens: ARKO_OLLAMA_NUM_CTX,
    usedTokens,
    usedChars,
    percent,
    approximation: true,
    categories,
  };
}

export function arkoContextHoverLabel(usage: ArkoContextUsage): string {
  const pct = Math.max(0, usage.percent);
  return `${pct}% · ~${formatArkoTokenCount(usage.usedTokens)} / ${formatArkoTokenCount(usage.limitTokens)} Tokens`;
}
